import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchSubscriptionPlans, createCheckoutSession } from 'utils/subscription'
import { clearClientSession } from 'utils/session'

/**
 * Modal to choose a subscription plan.
 * Fetches plans from /api/subscription/feature/ and submits to /api/subscription/.
 * @param {boolean} blocking - If true, user must select a plan (no close button, no close on backdrop/escape).
 */
export default function SubscriptionPlanModal({ open, onClose, blocking = false }) {
  const { t: tLayout } = useTranslation('layout')
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(null)
  // Compteur incrémenté par le bouton « Réessayer » : relance le chargement
  // des offres sans recharger toute la page.
  const [reloadKey, setReloadKey] = useState(0)
  const dispatch = useDispatch()
  // LAP-737: personalised welcome heading. Fall back gracefully if first_name
  // is missing (new user, anonymised account, etc.).
  const siteUser = useSelector((store) => store.user)
  const firstName = (siteUser?.user?.first_name || siteUser?.user?.prenom || '').trim()
  const welcomeTitle = firstName
    ? tLayout('wizardSubscription.welcomeNamed', { name: firstName })
    : tLayout('wizardSubscription.welcomeAnon')

  // LAP-752 — translate the canonical FR strings the backend may still return
  // for old tenants where the request.language path isn't honoured. Falls
  // back to the raw value (which the backend may already have translated).
  const translatePlanName = (raw, type) => {
    if (type) return tLayout(`wizardSubscription.planName.${type}`, { defaultValue: raw || '' })
    return raw
  }
  const translatePlanTagline = (raw, type) => {
    if (type) return tLayout(`wizardSubscription.planTagline.${type}`, { defaultValue: raw || '' })
    return raw
  }
  const translatePrice = (raw, type) => {
    if (type === 'free_trial') {
      return tLayout('wizardSubscription.freeTrialPrice', { defaultValue: raw || '' })
    }
    return raw
  }

  useEffect(() => {
    if (open) {
      setLoading(true)
      fetchSubscriptionPlans()
        .then((data) => setPlans(Array.isArray(data) ? data : []))
        .catch((e) => console.error('Failed to load plans:', e))
        .finally(() => setLoading(false))
    }
  }, [open, reloadKey])

  /**
   * Sortie de secours du mode bloquant.
   *
   * **Why:** en mode `blocking` la modale n'a ni croix ni bouton de fermeture.
   * Si l'appel aux offres échoue (session invalide, backend KO), l'écran
   * « Aucune offre disponible » devient un cul-de-sac : plus aucune action
   * possible, l'utilisateur est prisonnier de l'app. On lui laisse donc
   * toujours deux issues : réessayer, ou repartir sur la page de connexion
   * avec une session client propre.
   */
  const handleRetry = useCallback(() => setReloadKey((k) => k + 1), [])
  const handleSignOut = useCallback(() => {
    clearClientSession(dispatch)
    window.location.replace('/login')
  }, [dispatch])

  // Vrai quand la modale bloquante n'a rien à proposer : il FAUT alors offrir
  // une porte de sortie.
  const isBlockingDeadEnd = blocking && !loading && plans.length === 0

  const handleSelect = async (type) => {
    setSubmitting(type)
    try {
      const res = await createCheckoutSession(type)
      const ok = res?.status >= 200 && res?.status < 300
      const redirect = res?.data?.checkout_url || res?.data?.redirect_url

      if (ok && redirect) {
        window.location.href = redirect
        return
      }
      if (ok && res?.data?.success) {
        setSubmitting(null)
        if (res?.data?.message) alert(res.data.message)
        onClose?.()
        window.location.reload()
        return
      }

      setSubmitting(null)
      const msg = res?.data?.detail || res?.data?.message || tLayout('wizardSubscription.errorGeneric')
      alert(msg)
    } catch (e) {
      console.error('Failed to set subscription:', e)
      setSubmitting(null)
      alert(tLayout('wizardSubscription.errorRetry'))
    }
  }

  const handleClose = (e, reason) => {
    if (blocking) return
    onClose(e, reason)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={blocking}
      PaperProps={{
        sx: {
          background: 'rgba(15, 15, 25, 0.95)',
          backdropFilter: 'blur(30px)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          zIndex: (theme) => theme.zIndex.modal,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'white',
          fontSize: { xs: '1.3rem', md: '1.6rem' },
          fontWeight: 600,
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8)',
        }}
      >
        {tLayout('wizardSubscription.modalTitle')}
      </DialogTitle>
      <DialogContent
        sx={{
          color: 'rgba(255, 255, 255, 0.9)',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.3)', borderRadius: '4px' },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)',
        }}
      >
        {/* LAP-737: welcome block above the plan cards. Personalised with the
            user's first name when available; falls back to a generic greeting
            otherwise. */}
        <Box sx={{ textAlign: 'center', mb: 3, mt: 0.5, px: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 600,
              mb: 1,
              textShadow: '1px 1px 2px rgba(0,0,0,0.6)',
            }}
          >
            {welcomeTitle}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              lineHeight: 1.6,
              maxWidth: 480,
              mx: 'auto',
            }}
          >
            {tLayout('wizardSubscription.description')}
          </Typography>
        </Box>
        {loading ? (
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{tLayout('wizardSubscription.loading')}</Typography>
        ) : plans.length === 0 ? (
          <Box>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{tLayout('wizardSubscription.noPlans')}</Typography>
            {blocking && (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.55)', mt: 1 }}>
                {tLayout('wizardSubscription.noPlansHelp')}
              </Typography>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {plans.map((p) => (
              <Card
                key={p.type}
                variant="outlined"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '12px',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.12)',
                    borderColor: 'rgba(255, 255, 255, 0.25)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleSelect(p.type)}
                  disabled={!!submitting}
                  sx={{ borderRadius: '12px' }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      {translatePlanName(p.name, p.type)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                      {translatePlanTagline(p.feature, p.type) || '—'}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
                      {translatePrice(p.price, p.type)}
                    </Typography>
                    {submitting === p.type && (
                      <Typography variant="caption" sx={{ color: '#66c0ff', mt: 1, display: 'block' }}>
                        {tLayout('wizardSubscription.submitting')}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      {/* Mode bloquant SANS offre à proposer : on ne laisse jamais
          l'utilisateur enfermé — réessayer, ou repartir sur /login avec une
          session client purgée. */}
      {isBlockingDeadEnd && (
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            onClick={handleSignOut}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {tLayout('wizardSubscription.signOutButton')}
          </Button>
          <Button
            onClick={handleRetry}
            variant="outlined"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {tLayout('wizardSubscription.retryButton')}
          </Button>
        </DialogActions>
      )}
      {!blocking && (
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button
            onClick={() => onClose()}
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
            variant="outlined"
          >
            {tLayout('wizardSubscription.closeButton')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
